import { useFieldApi, useFieldState } from 'informed';
import { type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { capitalize } from '@/helpers/common';
import { ThemeMode } from '@/helpers/enums';
import { buildTestId, type WithTestId } from '@/helpers/testIds';
import type { Validator } from '@/helpers/validators';
import { useRegModalTheme } from '@/modules/registration/hooks/useRegModalTheme';
import { useGetCommonStore } from '@/store';
import { days, getYears, months } from './helpers';
import { Select } from './Select';

import { ScInputStructure } from './structures/styled';

interface InputDateProps extends WithTestId {
  name: string;
  disabled?: boolean;
  initialValue?: string;
  mode?: ThemeMode;
  validate?: Validator<any, any>;
}

export const InputDate: FC<InputDateProps> = ({
  name,
  disabled,
  initialValue,
  mode = ThemeMode.Light,
  validate,
  testId,
}) => {
  const { t } = useTranslation();
  const { styleScheme, select } = useRegModalTheme();
  const setError = () => 'dateError';

  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const countryCode = useCommonStore.use.countryCode();

  const [initialValueYear, initialValueMonth, initialValueDay] = useMemo(() => {
    if (!initialValue) return [];
    return initialValue.split('-');
  }, [initialValue]);

  const { day, month, year } = {
    day: `${name}.day`,
    month: `${name}.month`,
    year: `${name}.year`,
  };

  const stateDay = useFieldState(day);
  const stateMonth = useFieldState(month);
  const stateYear = useFieldState(year);
  const fieldDayApi = useFieldApi(day);
  const fieldMonthApi = useFieldApi(month);
  const fieldYearApi = useFieldApi(year);

  const date = useMemo(() => {
    if (!(stateDay && stateMonth && stateYear)) return;

    if (!(stateDay.value && stateMonth.value && stateYear.value)) {
      return;
    }

    return `${stateDay.value}/${stateMonth.value}/${stateYear.value}`;
  }, [stateDay, stateMonth, stateYear]);

  const validationError = useMemo(() => {
    if (!validate) return;
    return validate(date, undefined) as string;
  }, [date, validate]);

  const isAllTouched = useMemo(() => {
    if (!(stateDay && stateMonth && stateYear)) return false;
    return stateDay.touched && stateMonth.touched && stateYear.touched;
  }, [stateDay, stateMonth, stateYear]);

  const errorField = useMemo(() => {
    if (!(validationError && isAllTouched)) return;
    return validationError;
  }, [validationError, isAllTouched]);

  const monthsWithTrans = useMemo(
    () =>
      months.map(({ name, ...rest }) => ({
        ...rest,
        name: capitalize(t(`common:month.${name - 1}`)),
      })),
    [t]
  );

  const years = getYears(countryCode);

  const dateValidation = useMemo(
    () => (validationError ? setError : undefined),
    [validationError]
  );

  const dateFields = useMemo(
    () => [
      {
        key: 'day',
        initialValue: initialValueDay,
        placeholder: t('common:date.day'),
        options: days,
        hideErrorBorder: !(
          isAllTouched &&
          (!stateDay.value || (date && validationError))
        ),
      },
      {
        key: 'month',
        initialValue: initialValueMonth,
        placeholder: t('common:date.month'),
        options: monthsWithTrans,
        hideErrorBorder: !(
          isAllTouched &&
          (!stateMonth.value || (date && validationError))
        ),
      },
      {
        key: 'year',
        initialValue: initialValueYear,
        placeholder: t('common:date.year'),
        options: years,
        hideErrorBorder: !(
          isAllTouched &&
          (!stateYear.value || (date && validationError))
        ),
      },
    ],
    [
      initialValueDay,
      initialValueMonth,
      initialValueYear,
      isAllTouched,
      stateDay.value,
      stateMonth.value,
      stateYear.value,
      date,
      validationError,
      t,
      monthsWithTrans,
      years,
    ]
  );

  useEffect(() => {
    if (stateDay.value && stateMonth.value && stateYear.value) {
      fieldDayApi.validate();
      fieldMonthApi.validate();
      fieldYearApi.validate();
    }
  }, [
    fieldMonthApi,
    fieldDayApi,
    fieldYearApi,
    stateDay.value,
    stateMonth.value,
    stateYear.value,
  ]);

  return (
    <ScInputStructure>
      <ScInputDayOfBirthday>
        {dateFields.map(
          ({ key, initialValue, placeholder, options, hideErrorBorder }) => (
            <Select
              disabled={disabled}
              hideErrorBorder={hideErrorBorder}
              hideErrorMessage
              hideInputErrorColor
              initialValue={initialValue}
              key={key}
              mode={mode}
              name={`${name}.${key}`}
              native={isMobileDevice}
              options={options}
              placeholder={placeholder}
              styleScheme={styleScheme}
              testId={buildTestId(testId, key)}
              validate={dateValidation}
              validateOn="blur"
              withInsideLabel={select.withInsideLabel}
            />
          )
        )}
        {errorField && <div className="error">{t(errorField)}</div>}
      </ScInputDayOfBirthday>
    </ScInputStructure>
  );
};

const ScInputDayOfBirthday = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: 8px;
  grid-row-gap: 8px;

  & > div {
    width: 100%;

    &.error {
      grid-column: 1 / 4;
    }
  }
`;
